/**
 * LiveResults.tsx
 * Teacher-facing live results panel for the active session. Renders the
 * right view for whatever activity is currently presented:
 *  - Multiple Choice: live tally + Reveal Answer & Score
 *  - Word Cloud: word frequency list
 *  - Short Answer: response list
 *  - Drawing: submitted-drawings gallery
 * All types feed the session leaderboard, which can be posted to the slide.
 */

import React, { useEffect, useState } from 'react';
import { BarChart3, Check, Trophy } from 'lucide-react';
import Button from '@components/shared/Button';
import MCResults from '@components/presenter/LiveResults/MCResults';
import { useAppStore } from '@store/appStore';
import {
  subscribeMCResults,
  subscribeSubmissions,
  scoreSubmission,
  awardParticipation,
  getSessionLeaderboard,
} from '@db/submissionService';
import { updateRoomStatus } from '@db/roomService';
import { getCurrentSlideIndex, writeLeaderboardToSlide } from '@office/slideService';
import type { MCConfig, ActivityConfig } from '@types-local/quiz.types';
import type { MCResults as MCResultsType, Submission } from '@types-local/room.types';

function promptOf(config: ActivityConfig): string {
  if ('question' in config) return config.question;
  if ('prompt' in config) return config.prompt;
  // video_quiz never reaches here live (its questions are pushed as MC/short),
  // but keep the union exhaustive.
  return config.title ?? 'Interactive Video';
}

export default function LiveResults(): React.ReactElement {
  const activeRoom = useAppStore((s) => s.activeRoom);
  const config = (activeRoom?.activeConfig as ActivityConfig | null) ?? null;
  const roomCode = activeRoom?.roomCode ?? null;
  const type = config?.type ?? null;

  const [mcResults, setMcResults] = useState<MCResultsType | null>(null);
  const [submissions, setSubmissions] = useState<Submission[]>([]);
  const [revealed, setRevealed] = useState(false);
  const [working, setWorking] = useState(false);
  const [postingBoard, setPostingBoard] = useState(false);

  const promptKey = config ? promptOf(config) : null;

  // Reset reveal state whenever a new activity is pushed.
  useEffect(() => {
    setRevealed(false);
    setMcResults(null);
    setSubmissions([]);
  }, [promptKey]);

  // Subscribe to the right data source for the active activity type.
  useEffect(() => {
    if (!roomCode || !config) return;
    if (config.type === 'multiple_choice') {
      return subscribeMCResults(roomCode, config.options.length, setMcResults);
    }
    return subscribeSubmissions(roomCode, setSubmissions);
  }, [roomCode, config]);

  if (!activeRoom) {
    return (
      <div className="p-6 text-center space-y-2">
        <div className="text-4xl">📡</div>
        <p className="text-sm text-slate-700 font-medium">No active session</p>
        <p className="text-xs text-slate-500">Start a class session, then click "Start Presenting" to push an activity.</p>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="p-6 text-center space-y-2">
        <div className="text-4xl">⏳</div>
        <p className="text-sm text-slate-700 font-medium">Waiting for an activity</p>
        <p className="text-xs text-slate-500">Add an activity to a slide, then click "Start Presenting".</p>
      </div>
    );
  }

  const typedSubs = submissions.filter((s) => s.answer.type === type);
  const responseCount = type === 'multiple_choice' ? (mcResults?.totalResponses ?? 0) : typedSubs.length;

  const handleShowLeaderboard = async () => {
    if (!roomCode) return;
    setPostingBoard(true);
    try {
      const slideIndex = await getCurrentSlideIndex();
      const entries = await getSessionLeaderboard(roomCode);
      await writeLeaderboardToSlide(slideIndex, entries);
    } catch (err) {
      console.error('[GuroHub] Failed to write leaderboard to slide:', err);
    } finally {
      setPostingBoard(false);
    }
  };

  const handleRevealMC = async () => {
    if (!roomCode || !mcResults) return;
    setWorking(true);
    try {
      const ordered = [...mcResults.submissions].sort((a, b) => a.submittedAt - b.submittedAt);
      const firstRespondents = ordered.map((s) => s.studentId);
      for (const sub of ordered) {
        await scoreSubmission(roomCode, sub.studentId, activeRoom.classId, config as MCConfig, firstRespondents);
      }
      await updateRoomStatus(roomCode, 'results');
      setRevealed(true);
    } catch (err) {
      console.error('[GuroHub] Failed to reveal/score:', err);
    } finally {
      setWorking(false);
    }
  };

  const handleAwardParticipation = async () => {
    if (!roomCode) return;
    setWorking(true);
    try {
      await awardParticipation(roomCode);
      await updateRoomStatus(roomCode, 'results');
      setRevealed(true);
    } catch (err) {
      console.error('[GuroHub] Failed to award participation:', err);
    } finally {
      setWorking(false);
    }
  };

  const ICONS: Record<string, string> = {
    multiple_choice: '📊', word_cloud: '☁️', short_answer: '✏️', slide_drawing: '🎨', media_upload: '📸',
  };

  return (
    <div className="p-4 space-y-4">
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-xl bg-brand-500/20 border border-brand-500/30 flex items-center justify-center">
          <BarChart3 size={16} className="text-brand-400" />
        </div>
        <div>
          <h2 className="text-sm font-bold text-slate-900">Live Results · {responseCount} responses</h2>
          <p className="text-[10px] text-slate-500">{ICONS[type ?? ''] ?? ''} {promptOf(config)}</p>
        </div>
      </div>

      {/* Type-specific response view */}
      {type === 'multiple_choice' && (
        <MCResults
          config={revealed ? (config as MCConfig) : { ...(config as MCConfig), options: (config as MCConfig).options.map((o) => ({ ...o, isCorrect: false })) }}
          results={mcResults ?? { counts: (config as MCConfig).options.map(() => 0), totalResponses: 0, submissions: [] }}
        />
      )}

      {type === 'word_cloud' && (
        <WordFrequency submissions={typedSubs} />
      )}

      {type === 'short_answer' && (
        <div className="space-y-2">
          {typedSubs.length === 0 && <p className="text-xs text-slate-500 text-center py-4">No responses yet…</p>}
          {typedSubs.map((s, i) => (
            <div key={i} className="bg-surface-elevated border border-surface-border rounded-xl px-3 py-2">
              <p className="text-[10px] text-slate-500">{s.studentName}</p>
              <p className="text-sm text-slate-900">{s.answer.type === 'short_answer' ? s.answer.text : ''}</p>
            </div>
          ))}
        </div>
      )}

      {type === 'slide_drawing' && (
        <div className="grid grid-cols-2 gap-2">
          {typedSubs.length === 0 && <p className="col-span-2 text-xs text-slate-500 text-center py-4">No drawings yet…</p>}
          {typedSubs.map((s, i) => (
            <div key={i} className="bg-surface-elevated border border-surface-border rounded-xl p-1.5">
              {s.answer.type === 'slide_drawing' && (
                <img src={s.answer.imageDataUrl} alt={s.studentName} className="w-full rounded-lg bg-white" />
              )}
              <p className="text-[10px] text-slate-500 mt-1 text-center">{s.studentName}</p>
            </div>
          ))}
        </div>
      )}

      {type === 'media_upload' && (
        <div className="grid grid-cols-2 gap-2">
          {typedSubs.length === 0 && <p className="col-span-2 text-xs text-slate-500 text-center py-4">No photos yet…</p>}
          {typedSubs.map((s, i) => (
            <div key={i} className="bg-surface-elevated border border-surface-border rounded-xl p-1.5">
              {s.answer.type === 'media_upload' && (
                <a href={s.answer.fileUrl} target="_blank" rel="noreferrer">
                  <img src={s.answer.fileUrl} alt={s.studentName} className="w-full h-32 object-cover rounded-lg bg-white" />
                </a>
              )}
              <p className="text-[10px] text-slate-500 mt-1 text-center">{s.studentName}</p>
            </div>
          ))}
        </div>
      )}

      {/* Action area */}
      {revealed ? (
        <div className="space-y-3">
          <div className="flex items-center justify-center gap-2 text-xs text-green-400 font-semibold py-1">
            <Check size={14} /> {type === 'multiple_choice' ? 'Answer revealed & scored' : 'Participation awarded'}
          </div>
          <Button fullWidth variant="secondary" icon={<Trophy size={14} />} onClick={handleShowLeaderboard} loading={postingBoard}>
            Show Leaderboard on Slide
          </Button>
        </div>
      ) : (
        <Button
          fullWidth
          onClick={type === 'multiple_choice' ? handleRevealMC : handleAwardParticipation}
          loading={working}
          disabled={responseCount === 0}
        >
          {type === 'multiple_choice' ? 'Reveal Answer & Score' : 'Award Participation & Continue'}
        </Button>
      )}
    </div>
  );
}

/** Aggregates word-cloud submissions into a frequency list. */
function WordFrequency({ submissions }: { submissions: Submission[] }): React.ReactElement {
  const freq: Record<string, number> = {};
  for (const s of submissions) {
    if (s.answer.type !== 'word_cloud') continue;
    for (const w of s.answer.words) {
      const k = w.toLowerCase().trim();
      if (k) freq[k] = (freq[k] ?? 0) + 1;
    }
  }
  const entries = Object.entries(freq).sort((a, b) => b[1] - a[1]);
  if (entries.length === 0) return <p className="text-xs text-slate-500 text-center py-4">No words yet…</p>;
  const max = Math.max(...entries.map((e) => e[1]), 1);
  return (
    <div className="flex flex-wrap gap-2 justify-center py-2">
      {entries.map(([word, count]) => (
        <span
          key={word}
          className="text-orange-300 font-bold"
          style={{ fontSize: `${0.8 + (count / max) * 1.4}rem` }}
          title={`${count}`}
        >
          {word}
        </span>
      ))}
    </div>
  );
}
